import type {
  ApiError,
  BuildRequest,
  BuildResponse,
  CaptureRequest,
  CaptureResponse,
  ClaimQuestResponse,
  CollectCityResponse,
  GameBootstrapResponse,
  GuestAuthResponse,
  RecruitRequest,
  RecruitResponse,
  ResearchId,
  QuestId,
  StartResearchResponse,
  UpgradeBuildingRequest,
  UpgradeBuildingResponse,
} from "@acre/protocol";

const TOKEN_KEY = "acre.auth.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(path, { ...init, headers });
  const payload = (await response.json()) as T | ApiError;
  if (!response.ok) {
    const error = payload as ApiError;
    throw new Error(error.message || "Request failed");
  }
  return payload as T;
}

export const gameApi = {
  createGuest: () => apiRequest<GuestAuthResponse>("/api/auth/guest", { method: "POST" }),
  bootstrap: () => apiRequest<GameBootstrapResponse>("/api/game/bootstrap"),
  build: (request: BuildRequest) =>
    apiRequest<BuildResponse>("/api/city/build", { method: "POST", body: JSON.stringify(request) }),
  upgradeBuilding: (request: UpgradeBuildingRequest) =>
    apiRequest<UpgradeBuildingResponse>("/api/city/upgrade", { method: "POST", body: JSON.stringify(request) }),
  collectCity: (commandId: string) =>
    apiRequest<CollectCityResponse>("/api/city/collect", {
      method: "POST",
      body: JSON.stringify({ commandId }),
    }),
  recruit: (request: RecruitRequest) =>
    apiRequest<RecruitResponse>("/api/army/recruit", { method: "POST", body: JSON.stringify(request) }),
  startResearch: (commandId: string, researchId: ResearchId) =>
    apiRequest<StartResearchResponse>("/api/research/start", {
      method: "POST",
      body: JSON.stringify({ commandId, researchId }),
    }),
  claimQuest: (commandId: string, questId: QuestId) =>
    apiRequest<ClaimQuestResponse>("/api/quest/claim", {
      method: "POST",
      body: JSON.stringify({ commandId, questId }),
    }),
  capture: (request: CaptureRequest) =>
    apiRequest<CaptureResponse>("/api/world/capture", { method: "POST", body: JSON.stringify(request) }),
};
